// ─────────────────────────────────────────────────────────────────────────────
// stores/locationStore.ts – Standort (Location) Pinia Store
//
// Abonniert die top-level Collection `locations`, gefiltert nach dem aktuell
// gewaehlten Mandanten (companyId). Wie die anderen Stores wird die vorige
// Subscription VOR dem Resubscribe abgeraeumt (Company-Switch-Bug-Muster).
//
// REQ-RS-042: «Standort offline» wird REIN CLIENTSEITIG aus dem Status der
// zugehoerigen Druckserver abgeleitet – es gibt KEIN persistiertes
// `locations.status`-Feld. Die Ableitung liest aus dem bestehenden
// printserverStore (onSnapshot auf `printservers`), kein eigener Query je
// Standort.
// ─────────────────────────────────────────────────────────────────────────────

import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  collection, query, where, orderBy,
  addDoc, updateDoc, deleteDoc, doc, serverTimestamp,
  type QuerySnapshot, type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/firebase/config';
import { liveAbo } from '@/utils/liveAbo';
import type { Location } from '@/firebase/models';
import { usePrintserverStore } from '@/stores/printserverStore';

/** Pflegbare Felder eines Standorts (REQ-DM-011); ohne System-/Zeitfelder. */
export type LocationInput = Omit<Location, 'id' | 'createdAt' | 'updatedAt'>;

export const useLocationStore = defineStore('locations', () => {
  const locations = ref<Location[]>([]);
  const isLoading = ref(false);
  const error     = ref<string | null>(null);
  /** Haelt der Store gerade einen Listener (REQ-OPS-053 (a) (ii), OP-151)? */
  const isSubscribed = ref(false);

  let _unsub: Unsubscribe | null = null;

  function subscribe(companyId: string): void {
    // Vorige Subscription abraeumen, bevor neu abonniert wird (Company-Switch).
    // Das bricht zugleich eine laufende Rueckzugsstaffel ab (REQ-OPS-053 (d)).
    if (_unsub) { _unsub(); _unsub = null; }
    isLoading.value = true;
    error.value     = null;

    const q = query(
      collection(db, 'locations'),
      where('companyId', '==', companyId),
      orderBy('name'),
    );
    isSubscribed.value = true;
    // Fehlerweg, Protokoll und Staffel kommen aus der EINEN Bildungsstelle
    // (REQ-OPS-053 (e), T-M21.4).
    _unsub = liveAbo<QuerySnapshot>({
      collection: 'locations',
      companyId,
      ziel: () => q,
      onData: (snap) => {
        locations.value = snap.docs.map(d => ({ id: d.id, ...d.data() } as Location));
        isLoading.value = false;
      },
      onFehler: (err) => {
        error.value        = err.message;
        isLoading.value    = false;
        isSubscribed.value = false;
      },
      // Der Rueckweg (REQ-OPS-053 (a), Gegenrichtung): kommt die
      // Wiederaufnahme durch, faellt auch der Fehlzustand.
      onErholt: () => {
        error.value        = null;
        isSubscribed.value = true;
      },
      // Abmeldung: kein Fehlzustand, nur die Wahrheit ueber das Abo.
      onStill: () => {
        isLoading.value    = false;
        isSubscribed.value = false;
      },
    });
  }

  function unsubscribe(): void {
    _unsub?.();
    _unsub             = null;
    locations.value    = [];
    isSubscribed.value = false;
  }

  /**
   * REQ-RS-042: Ein Standort gilt als offline, wenn er MINDESTENS EINEN
   * Druckserver hat und ALLE seine Druckserver `offline` sind. Der
   * `length >= 1`-Guard ist zwingend: die leere Menge (kein Druckserver)
   * darf NICHT als offline gelten.
   */
  function isLocationOffline(locationId: string): boolean {
    const printserverStore = usePrintserverStore();
    const servers = printserverStore.servers.filter(s => s.locationId === locationId);
    return servers.length >= 1 && servers.every(s => s.status === 'offline');
  }

  // ── CRUD (REQ-DM-011) ──────────────────────────────────────────────────────
  async function addLocation(data: LocationInput): Promise<string> {
    const ref = await addDoc(collection(db, 'locations'), {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return ref.id;
  }

  async function updateLocation(id: string, data: Partial<LocationInput>): Promise<void> {
    await updateDoc(doc(db, 'locations', id), { ...data, updatedAt: serverTimestamp() });
  }

  async function deleteLocation(id: string): Promise<void> {
    await deleteDoc(doc(db, 'locations', id));
  }

  return {
    locations, isLoading, error, isSubscribed,
    subscribe, unsubscribe, isLocationOffline,
    addLocation, updateLocation, deleteLocation,
  };
});
